import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Shield, 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Save, 
  Edit2,
  Link2,
  X,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReviewAgents = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reviewAgents, setReviewAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    system_prompt: '',
    model_name: 'anthropic/claude-sonnet-4-5-20250929',
    pass_condition: '評分 >= 7 分且無重大問題',
    max_retries: 3
  });
  
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/create');
      return;
    }
    fetchReviewAgents();
    fetchOpenRouterModels();
  }, [user, navigate]);
  
  const fetchOpenRouterModels = async () => {
    setLoadingModels(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/openrouter-models`);
      setOpenrouterModels(response.data);
    } catch (error) {
      console.error('獲取模型列表失敗:', error);
    } finally {
      setLoadingModels(false);
    }
  };

  const fetchReviewAgents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/review-agents`);
      setReviewAgents(response.data);
    } catch (error) {
      console.error('獲取審核 Agent 失敗:', error);
      toast.error('獲取審核 Agent 列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.system_prompt) {
      toast.error('請填寫名稱和系統提示詞');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/review-agents`, formData);
      toast.success('審核 Agent 創建成功！');
      setShowCreateForm(false);
      resetForm();
      fetchReviewAgents();
    } catch (error) {
      console.error('創建失敗:', error);
      toast.error('創建失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/review-agents/${id}`, formData);
      toast.success('更新成功！');
      setEditingAgent(null);
      resetForm();
      fetchReviewAgents();
    } catch (error) {
      console.error('更新失敗:', error);
      toast.error('更新失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name, linkedCount) => {
    if (linkedCount > 0) {
      toast.error(`無法刪除：有 ${linkedCount} 個 Agent 正在使用此審核 Agent`);
      return;
    }

    if (!window.confirm(`確定要刪除「${name}」嗎？`)) return;

    try {
      await axios.delete(`${API_URL}/api/admin/review-agents/${id}`);
      toast.success('刪除成功！');
      fetchReviewAgents();
    } catch (error) {
      console.error('刪除失敗:', error);
      toast.error(error.response?.data?.detail || '刪除失敗');
    }
  };

  const startEdit = (agent) => {
    setEditingAgent(agent.id);
    setFormData({
      name: agent.name,
      description: agent.description,
      system_prompt: agent.system_prompt,
      model_name: agent.model_name || 'anthropic/claude-sonnet-4-5-20250929',
      pass_condition: agent.pass_condition,
      max_retries: agent.max_retries
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      system_prompt: '',
      model_name: 'anthropic/claude-sonnet-4-5-20250929',
      pass_condition: '評分 >= 7 分且無重大問題',
      max_retries: 3
    });
  };

  const cancelEdit = () => {
    setEditingAgent(null);
    setShowCreateForm(false);
    resetForm();
  };

  if (user?.role !== 'admin') return null;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/create')}
              className="rounded-full"
              data-testid="back-to-create-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回創作
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-heading font-bold text-neutral-900">審核 Agent 管理</h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/review-logs')}
              className="rounded-full"
              data-testid="view-review-logs-btn"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              審核歷程
            </Button>
            <Button
              onClick={() => setShowCreateForm(true)}
              className="rounded-full"
              data-testid="create-review-agent-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              新增審核 Agent
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Info Banner */}
        <Card className="p-4 mb-6 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-900">什麼是審核 Agent？</h3>
              <p className="text-sm text-amber-700 mt-1">
                審核 Agent 是一個專門負責檢查內容品質的智能體。當主 Agent 生成內容時，
                系統會自動將內容發送給審核 Agent 檢查，確保輸出符合您設定的品質標準。
                審核過程對用戶是透明的。
              </p>
            </div>
          </div>
        </Card>

        {/* Create Form */}
        {showCreateForm && (
          <Card className="p-6 mb-6" data-testid="create-review-agent-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-heading font-bold">新增審核 Agent</h2>
              <Button variant="ghost" size="sm" onClick={cancelEdit}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <ReviewAgentForm
              formData={formData}
              setFormData={setFormData}
              onSave={handleCreate}
              onCancel={cancelEdit}
              saving={saving}
              openrouterModels={openrouterModels}
              loadingModels={loadingModels}
            />
          </Card>
        )}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-neutral-600">載入中...</p>
          </div>
        ) : reviewAgents.length === 0 && !showCreateForm ? (
          /* Empty State */
          <Card className="p-12 text-center">
            <Shield className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 mb-2">尚未建立審核 Agent</h3>
            <p className="text-neutral-600 mb-6">
              創建您的第一個審核 Agent，開始自動化內容品質管理
            </p>
            <Button onClick={() => setShowCreateForm(true)} className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              新增審核 Agent
            </Button>
          </Card>
        ) : (
          /* Agent List */
          <div className="space-y-4">
            {reviewAgents.map((agent) => (
              <Card key={agent.id} className="p-6" data-testid={`review-agent-card-${agent.id}`}>
                {editingAgent === agent.id ? (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-heading font-bold">編輯審核 Agent</h3>
                      <Button variant="ghost" size="sm" onClick={cancelEdit}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <ReviewAgentForm
                      formData={formData}
                      setFormData={setFormData}
                      onSave={() => handleUpdate(agent.id)}
                      onCancel={cancelEdit}
                      saving={saving}
                      openrouterModels={openrouterModels}
                      loadingModels={loadingModels}
                    />
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                          <Shield className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-heading font-bold text-neutral-900">
                            {agent.name}
                          </h3>
                          <p className="text-sm text-neutral-600 mt-1">{agent.description}</p>
                          
                          <div className="flex items-center gap-4 mt-3 text-sm">
                            <span className="flex items-center gap-1 text-neutral-500">
                              <Link2 className="w-4 h-4" />
                              已連結 {agent.linked_agents_count} 個 Agent
                            </span>
                            <span className="text-neutral-400">|</span>
                            <span className="text-neutral-500">
                              最大重試：{agent.max_retries} 次
                            </span>
                            <span className="text-neutral-400">|</span>
                            <span className="text-neutral-500 font-mono text-xs">
                              {agent.model_name || 'anthropic/claude-sonnet-4-5-20250929'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEdit(agent)}
                          className="rounded-full"
                          data-testid={`edit-review-agent-${agent.id}-btn`}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          編輯
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(agent.id, agent.name, agent.linked_agents_count)}
                          className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={agent.linked_agents_count > 0}
                          data-testid={`delete-review-agent-${agent.id}-btn`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Preview System Prompt */}
                    <div className="mt-4 p-4 bg-neutral-50 rounded-lg">
                      <h4 className="text-sm font-medium text-neutral-700 mb-2">系統提示詞預覽</h4>
                      <p className="text-sm text-neutral-600 whitespace-pre-wrap line-clamp-3">
                        {agent.system_prompt}
                      </p>
                    </div>

                    {/* Pass Condition */}
                    <div className="mt-3 p-3 bg-green-50 rounded-lg">
                      <span className="text-sm font-medium text-green-800">通過條件：</span>
                      <span className="text-sm text-green-700 ml-2">{agent.pass_condition}</span>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

// Form Component
const ReviewAgentForm = ({ formData, setFormData, onSave, onCancel, saving, openrouterModels, loadingModels }) => {
  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">名稱 *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="例：品質審核員"
            data-testid="review-agent-name-input"
          />
        </div>
        <div>
          <Label htmlFor="max_retries">最大審核次數</Label>
          <Input
            id="max_retries"
            type="number"
            min={1}
            max={10}
            value={formData.max_retries}
            onChange={(e) => setFormData({ ...formData, max_retries: parseInt(e.target.value) || 3 })}
            data-testid="review-agent-max-retries-input"
          />
          <p className="text-xs text-neutral-500 mt-1">審核不通過時的最大重試次數</p>
        </div>
      </div>

      <div>
        <Label htmlFor="description">用途說明</Label>
        <Input
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="例：檢查腳本品質、格式規範、可讀性"
          data-testid="review-agent-description-input"
        />
      </div>

      {/* Model Selection */}
      <div>
        <Label htmlFor="model_name">AI 模型 *</Label>
        <Select
          value={formData.model_name}
          onValueChange={(value) => setFormData({ ...formData, model_name: value })}
          disabled={loadingModels}
        >
          <SelectTrigger 
            className="w-full bg-white"
            data-testid="review-agent-model-select"
          >
            <SelectValue placeholder={loadingModels ? "載入模型中..." : "選擇 AI 模型"} />
          </SelectTrigger>
          <SelectContent className="bg-white max-h-[300px]">
            {openrouterModels.map((model) => (
              <SelectItem 
                key={model.id} 
                value={model.id}
                className="bg-white hover:bg-amber-50"
              >
                <div className="flex flex-col">
                  <span className="font-medium">{model.name}</span>
                  <span className="text-xs text-neutral-500">
                    輸入: ${(model.pricing.prompt * 1000000).toFixed(2)}/1M tokens | 
                    輸出: ${(model.pricing.completion * 1000000).toFixed(2)}/1M tokens
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-neutral-500 mt-1">
          選擇用於執行審核的 AI 模型（價格和性能可能不同）
        </p>
      </div>

      {/* System Prompt */}
      <div>
        <Label htmlFor="system_prompt">系統提示詞 (審核指令) *</Label>
        <Textarea
          id="system_prompt"
          value={formData.system_prompt}
          onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
          className="min-h-[250px] font-mono text-sm"
          placeholder={`你是一個內容品質審核員。你的任務是審核以下內容並給出審核報告。

審核標準：
1. 結構完整性：是否有開場、主體、結尾
2. 語句通順：是否有語病或錯字
3. 吸引力：開頭是否能抓住觀眾注意力
4. 格式規範：是否符合腳本格式要求

請根據以上標準進行審核。`}
          data-testid="review-agent-system-prompt-input"
        />
        <p className="text-xs text-neutral-500 mt-1">
          定義審核 Agent 的審核邏輯和評估標準
        </p>
      </div>

      {/* Pass Condition */}
      <div>
        <Label htmlFor="pass_condition">通過條件</Label>
        <Input
          id="pass_condition"
          value={formData.pass_condition}
          onChange={(e) => setFormData({ ...formData, pass_condition: e.target.value })}
          placeholder="例：評分 >= 7 分且無重大問題"
          data-testid="review-agent-pass-condition-input"
        />
        <p className="text-xs text-neutral-500 mt-1">
          描述什麼情況下內容視為通過審核（僅供參考顯示）
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-4 border-t">
        <Button
          onClick={onSave}
          disabled={saving}
          className="rounded-full"
          data-testid="save-review-agent-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? '儲存中...' : '儲存'}
        </Button>
        <Button
          variant="outline"
          onClick={onCancel}
          className="rounded-full"
          data-testid="cancel-review-agent-btn"
        >
          取消
        </Button>
      </div>
    </div>
  );
};

export default AdminReviewAgents;
